import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function MagicLinkExpired() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const handleResend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error('Please enter your email address');
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase.auth.signInWithOtp({
                email: email.trim(),
                options: {
                    emailRedirectTo: `${window.location.origin}/dashboard`,
                },
            });

            if (error) throw error;

            setSent(true);
            toast.success('New magic link sent! Check your email.');
        } catch (err: any) {
            console.error('[MagicLinkExpired] Failed to send:', err);
            toast.error(err.message || 'Failed to send magic link');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-sl-bg flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                <div className="bg-sl-surface rounded-2xl border border-sl-surface-2 p-8 text-center">
                    {/* Icon */}
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fas fa-clock text-3xl text-red-400"></i>
                    </div>

                    <h1 className="text-2xl font-bold text-white mb-3">
                        Magic Link Expired
                    </h1>

                    <p className="text-sl-muted mb-6">
                        This magic link has expired. Magic links are valid for <strong className="text-white">1 hour</strong> after being sent.
                    </p>

                    {sent ? (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
                            <i className="fas fa-check-circle text-green-400 mb-2 text-xl"></i>
                            <p className="text-green-300">
                                New magic link sent to <strong>{email}</strong>
                            </p>
                            <p className="text-sm text-green-400/70 mt-2">
                                Check your inbox and spam folder
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleResend} className="space-y-4">
                            <div>
                                <label className="block text-sm text-sl-muted mb-2 text-left">
                                    Enter your email to get a fresh link
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    className="w-full px-4 py-3 rounded-xl bg-sl-surface-2 border border-sl-surface-2 text-white placeholder-sl-muted focus:border-sl-accent focus:outline-none transition-colors"
                                    autoFocus
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 px-4 rounded-xl bg-sl-accent text-white font-semibold hover:bg-sl-accent/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane"></i>
                                        Send New Magic Link
                                    </>
                                )}
                            </button>
                        </form>
                    )}

                    <div className="mt-6 pt-6 border-t border-sl-surface-2">
                        <p className="text-sm text-sl-muted">
                            Know your password?{' '}
                            <Link to="/login" className="text-sl-accent hover:underline">
                                Log in instead
                            </Link>
                        </p>
                    </div>
                </div>

                {/* Tips */}
                <div className="mt-6 text-center text-sm text-sl-muted">
                    <p>
                        <i className="fas fa-lightbulb text-yellow-400 mr-2"></i>
                        Tip: Always use the most recent magic link email
                    </p>
                </div>
            </div>
        </div>
    );
}
